/**
 * SommelAI Intelligent Pairing System
 * Phase 2: Core AI Features - Food-Beverage Pairing Engine
 *
 * This service provides AI-powered food and beverage pairing recommendations
 * with confidence scoring, explanations, and contextual analysis.
 */

import { aiServiceIntegration } from "./aiServiceIntegration.js";

class IntelligentPairingSystem {
  constructor() {
    this.pairingCache = new Map();
    this.menuAnalysisCache = new Map();
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
  }

  /**
   * Analyze a restaurant's food menu to extract relevant pairing information
   * @param {Array} menuItems - Array of food menu items
   * @param {Object} options - Analysis options
   * @returns {Object} Analyzed menu data optimized for pairing
   */
  async analyzeFoodMenu(menuItems, options = {}) {
    const cacheKey = JSON.stringify({
      menuItems: menuItems.map((item) => item.id || item.name),
      options,
    });

    // Check cache first
    if (this.menuAnalysisCache.has(cacheKey)) {
      const cached = this.menuAnalysisCache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheExpiry) {
        return cached.data;
      }
    }

    try {
      const prompt = this.buildMenuAnalysisPrompt(menuItems, options);
      const analysis = await aiServiceIntegration.enhanceBeverage(prompt, {
        type: "menu_analysis",
        includeRatings: false,
        includePairings: false,
        customPrompt: true,
      });

      const processedAnalysis = this.processMenuAnalysis(analysis, menuItems);

      // Cache the result
      this.menuAnalysisCache.set(cacheKey, {
        data: processedAnalysis,
        timestamp: Date.now(),
      });

      return processedAnalysis;
    } catch (error) {
      console.error("Error analyzing food menu:", error);
      return this.fallbackMenuAnalysis(menuItems);
    }
  }

  /**
   * Generate specific pairings between beverages and food items
   * @param {Object} beverage - Beverage to pair
   * @param {Array} menuItems - Food items to pair with
   * @param {Object} context - Additional context (occasion, preferences, etc.)
   * @returns {Array} Array of pairing recommendations with confidence scores
   */
  async generatePairings(beverage, menuItems, context = {}) {
    const cacheKey = JSON.stringify({
      beverageId: beverage.id || beverage.name,
      menuItems: menuItems.map((item) => item.id || item.name),
      context,
    });

    // Check cache first
    if (this.pairingCache.has(cacheKey)) {
      const cached = this.pairingCache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheExpiry) {
        return cached.data;
      }
    }

    try {
      const pairings = [];

      // Generate pairings for each menu item
      for (const menuItem of menuItems.slice(0, 20)) {
        // Limit to prevent API overuse
        const pairing = await this.generateSinglePairing(
          beverage,
          menuItem,
          context
        );
        if (pairing) {
          pairings.push(pairing);
        }
      }

      // Sort by confidence score
      pairings.sort((a, b) => b.confidence - a.confidence);

      // Cache the result
      this.pairingCache.set(cacheKey, {
        data: pairings,
        timestamp: Date.now(),
      });

      return pairings;
    } catch (error) {
      console.error("Error generating pairings:", error);
      return this.fallbackPairings(beverage, menuItems);
    }
  }

  /**
   * Generate detailed explanation for why a specific beverage pairs well with a dish
   * @param {Object} beverage - The beverage
   * @param {Object} dish - The food dish
   * @param {Object} context - Additional context
   * @returns {Object} Detailed pairing explanation
   */
  async explainPairing(beverage, dish, context = {}) {
    try {
      const prompt = this.buildPairingExplanationPrompt(
        beverage,
        dish,
        context
      );
      const explanation = await aiServiceIntegration.enhanceBeverage(prompt, {
        type: "pairing_explanation",
        includeRatings: false,
        includePairings: false,
        customPrompt: true,
      });

      return this.processPairingExplanation(explanation, beverage, dish);
    } catch (error) {
      console.error("Error explaining pairing:", error);
      return this.fallbackExplanation(beverage, dish);
    }
  }

  /**
   * Generate cocktail recipes using restaurant's spirits inventory
   * @param {Array} spirits - Available spirits
   * @param {Object} preferences - Style preferences and constraints
   * @returns {Array} Array of cocktail recommendations
   */
  async generateCocktailRecipes(spirits, preferences = {}) {
    try {
      const prompt = this.buildCocktailGenerationPrompt(spirits, preferences);
      const recipes = await aiServiceIntegration.enhanceBeverage(prompt, {
        type: "cocktail_generation",
        includeRatings: false,
        includePairings: true,
        customPrompt: true,
      });

      return this.processCocktailRecipes(recipes, spirits);
    } catch (error) {
      console.error("Error generating cocktail recipes:", error);
      return this.fallbackCocktailRecipes(spirits);
    }
  }

  /**
   * Get contextual pairing suggestions based on occasion, season, etc.
   * @param {Object} context - Context information
   * @param {Array} inventory - Available beverage inventory
   * @returns {Array} Contextual recommendations
   */
  async getContextualPairings(context, inventory) {
    try {
      const prompt = this.buildContextualPairingPrompt(context, inventory);
      const suggestions = await aiServiceIntegration.enhanceBeverage(prompt, {
        type: "contextual_pairing",
        includeRatings: false,
        includePairings: true,
        customPrompt: true,
      });

      return this.processContextualPairings(suggestions, context, inventory);
    } catch (error) {
      console.error("Error getting contextual pairings:", error);
      return this.fallbackContextualPairings(context, inventory);
    }
  }

  // Private helper methods

  buildMenuAnalysisPrompt(menuItems, options) {
    return `
Analyze this restaurant menu for beverage pairing optimization. Extract key information for each dish:

MENU ITEMS:
${menuItems
  .map(
    (item) => `
- ${item.name || "Unnamed Dish"}
  Description: ${item.description || "No description"}
  Price: ${item.price || "Not specified"}
  Category: ${item.category || "Uncategorized"}
  Ingredients: ${
    item.ingredients ? item.ingredients.join(", ") : "Not specified"
  }
`
  )
  .join("")}

ANALYSIS REQUIREMENTS:
1. Cuisine Style & Regional Influences
2. Cooking Methods & Flavor Profiles
3. Key Ingredients & Seasonings
4. Dish Intensity Levels (light, medium, bold)
5. Pairing Categories (wines, beers, spirits, cocktails)
6. Special Dietary Considerations
7. Seasonal Appropriateness

OPTIONS: ${JSON.stringify(options)}

Return detailed analysis in structured JSON format focusing on pairing-relevant characteristics.
`;
  }

  buildPairingExplanationPrompt(beverage, dish, context) {
    return `
Provide a detailed explanation for this food and beverage pairing:

BEVERAGE:
- Name: ${beverage.name}
- Type: ${beverage.type}
- Description: ${beverage.description || ""}
- Enhanced Data: ${JSON.stringify(beverage.aiEnhanced || {}, null, 2)}

DISH:
- Name: ${dish.name}
- Description: ${dish.description || ""}
- Category: ${dish.category || ""}
- Ingredients: ${
      dish.ingredients ? dish.ingredients.join(", ") : "Not specified"
    }

CONTEXT:
${JSON.stringify(context, null, 2)}

EXPLANATION REQUIREMENTS:
1. Flavor Harmony Analysis
2. Texture & Mouthfeel Interaction
3. Complement vs. Contrast Strategy
4. Cultural & Traditional Pairing Rationale
5. Serving Recommendations
6. Alternative Pairing Options
7. Confidence Score (0-1) with reasoning

Provide expert-level sommelier insights in an accessible format.
`;
  }

  buildCocktailGenerationPrompt(spirits, preferences) {
    return `
Create cocktail recipes using these available spirits:

AVAILABLE SPIRITS:
${spirits
  .map(
    (spirit) => `
- ${spirit.name}
  Type: ${spirit.type || spirit.category}
  Description: ${spirit.description || ""}
  Enhanced Info: ${JSON.stringify(spirit.aiEnhanced || {}, null, 2)}
`
  )
  .join("")}

PREFERENCES:
${JSON.stringify(preferences, null, 2)}

COCKTAIL REQUIREMENTS:
1. Use only available spirits (max 2 per cocktail)
2. Include common bar ingredients (simple syrup, citrus, bitters, etc.)
3. Provide difficulty levels (Easy, Intermediate, Advanced)
4. Include preparation time and technique
5. Suggest garnishes and glassware
6. Food pairing suggestions
7. Seasonal appropriateness
8. Profit margin considerations

Generate 5-8 unique cocktail recipes with complete specifications.
`;
  }

  buildContextualPairingPrompt(context, inventory) {
    return `
Suggest beverage pairings based on this context and available inventory:

CONTEXT:
- Occasion: ${context.occasion || "General dining"}
- Season: ${context.season || "Any"}
- Time of Day: ${context.timeOfDay || "Evening"}
- Group Size: ${context.groupSize || "Not specified"}
- Budget Range: ${context.budget || "Flexible"}
- Special Requirements: ${context.requirements || "None"}
- Weather: ${context.weather || "Not specified"}

AVAILABLE INVENTORY:
${inventory
  .slice(0, 50)
  .map(
    (item) => `
- ${item.name} (${item.type})
  Price: ${item.price || "Not specified"}
  Enhanced: ${item.aiEnhanced ? "Yes" : "No"}
`
  )
  .join("")}

RECOMMENDATION REQUIREMENTS:
1. Context-appropriate selections
2. Variety across categories
3. Price point distribution
4. Pairing versatility
5. Seasonal alignment
6. Occasion suitability
7. Group dining considerations

Provide 8-12 recommendations with detailed reasoning.
`;
  }

  async generateSinglePairing(beverage, menuItem, context) {
    try {
      const prompt = `
Analyze this specific pairing and provide a confidence score:

BEVERAGE: ${beverage.name} (${beverage.type})
${beverage.aiEnhanced ? JSON.stringify(beverage.aiEnhanced, null, 2) : ""}

DISH: ${menuItem.name}
Description: ${menuItem.description || ""}
Category: ${menuItem.category || ""}

CONTEXT: ${JSON.stringify(context)}

Provide:
1. Confidence score (0-1)
2. Brief pairing rationale (2-3 sentences)
3. Serving suggestions
4. Alternative if this pairing doesn't work

Response in JSON format.
`;

      const result = await aiServiceIntegration.enhanceBeverage(prompt, {
        type: "single_pairing",
        customPrompt: true,
      });

      return this.processSinglePairing(result, beverage, menuItem);
    } catch (error) {
      console.error("Error generating single pairing:", error);
      return null;
    }
  }

  processMenuAnalysis(analysis, menuItems) {
    try {
      // Parse AI response and structure menu analysis
      const parsed =
        typeof analysis === "string" ? JSON.parse(analysis) : analysis;

      return {
        cuisineStyle: parsed.cuisineStyle || "Mixed",
        primaryFlavors: parsed.primaryFlavors || [],
        intensityDistribution: parsed.intensityDistribution || {
          light: 0,
          medium: 0,
          bold: 0,
        },
        pairingCategories: parsed.pairingCategories || {
          wines: { recommended: [], avoid: [] },
          beers: { recommended: [], avoid: [] },
          spirits: { recommended: [], avoid: [] },
          cocktails: { recommended: [], avoid: [] },
        },
        specialConsiderations: parsed.specialConsiderations || [],
        seasonalAlignment: parsed.seasonalAlignment || "Year-round",
        timestamp: Date.now(),
        menuItemCount: menuItems.length,
      };
    } catch (error) {
      console.error("Error processing menu analysis:", error);
      return this.fallbackMenuAnalysis(menuItems);
    }
  }

  processPairingExplanation(explanation, beverage, dish) {
    try {
      const parsed =
        typeof explanation === "string" ? JSON.parse(explanation) : explanation;

      return {
        beverage: { name: beverage.name, type: beverage.type },
        dish: { name: dish.name, category: dish.category },
        flavorHarmony: parsed.flavorHarmony || "Complementary pairing",
        textureInteraction:
          parsed.textureInteraction || "Balanced texture profile",
        pairingStrategy: parsed.pairingStrategy || "complement",
        culturalRationale:
          parsed.culturalRationale || "Traditional pairing approach",
        servingRecommendations: parsed.servingRecommendations || [],
        alternatives: parsed.alternatives || [],
        confidence: parsed.confidence || 0.7,
        confidenceReasoning:
          parsed.confidenceReasoning || "Based on flavor compatibility",
        timestamp: Date.now(),
      };
    } catch (error) {
      console.error("Error processing pairing explanation:", error);
      return this.fallbackExplanation(beverage, dish);
    }
  }

  processCocktailRecipes(recipes, spirits) {
    try {
      const parsed =
        typeof recipes === "string" ? JSON.parse(recipes) : recipes;
      const cocktails = Array.isArray(parsed) ? parsed : parsed.cocktails || [];

      return cocktails.map((cocktail) => ({
        name: cocktail.name || "Unnamed Cocktail",
        description: cocktail.description || "",
        ingredients: cocktail.ingredients || [],
        instructions: cocktail.instructions || [],
        glassware: cocktail.glassware || "Rocks glass",
        garnish: cocktail.garnish || "None",
        difficulty: cocktail.difficulty || "Intermediate",
        prepTime: cocktail.prepTime || "3-5 minutes",
        spiritsUsed: cocktail.spiritsUsed || [],
        foodPairings: cocktail.foodPairings || [],
        seasonalFit: cocktail.seasonalFit || "Year-round",
        profitMargin: cocktail.profitMargin || "Good",
        technique: cocktail.technique || "Build",
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error("Error processing cocktail recipes:", error);
      return this.fallbackCocktailRecipes(spirits);
    }
  }

  processContextualPairings(suggestions, context, inventory) {
    try {
      const parsed =
        typeof suggestions === "string" ? JSON.parse(suggestions) : suggestions;
      const recommendations = Array.isArray(parsed)
        ? parsed
        : parsed.recommendations || [];

      return recommendations.map((rec) => ({
        beverage: rec.beverage || {},
        reasoning: rec.reasoning || "Contextually appropriate",
        contextAlignment: rec.contextAlignment || 0.8,
        versatility: rec.versatility || 0.7,
        pricePoint: rec.pricePoint || "moderate",
        seasonalFit: rec.seasonalFit || context.season || "appropriate",
        occasionSuitability:
          rec.occasionSuitability || context.occasion || "suitable",
        groupDiningScore: rec.groupDiningScore || 0.8,
        alternatives: rec.alternatives || [],
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error("Error processing contextual pairings:", error);
      return this.fallbackContextualPairings(context, inventory);
    }
  }

  processSinglePairing(result, beverage, menuItem) {
    try {
      const parsed = typeof result === "string" ? JSON.parse(result) : result;

      return {
        beverage: { name: beverage.name, type: beverage.type },
        dish: { name: menuItem.name, category: menuItem.category },
        confidence: parsed.confidence || 0.6,
        rationale: parsed.rationale || "Complementary flavors",
        servingSuggestions: parsed.servingSuggestions || [],
        alternatives: parsed.alternatives || [],
        timestamp: Date.now(),
      };
    } catch (error) {
      console.error("Error processing single pairing:", error);
      return {
        beverage: { name: beverage.name, type: beverage.type },
        dish: { name: menuItem.name, category: menuItem.category },
        confidence: 0.5,
        rationale: "Basic pairing compatibility",
        servingSuggestions: ["Serve at appropriate temperature"],
        alternatives: [],
        timestamp: Date.now(),
      };
    }
  }

  // Fallback methods for error handling

  fallbackMenuAnalysis(menuItems) {
    return {
      cuisineStyle: "Mixed Contemporary",
      primaryFlavors: ["savory", "umami", "fresh"],
      intensityDistribution: { light: 30, medium: 50, bold: 20 },
      pairingCategories: {
        wines: { recommended: ["white", "red", "rosé"], avoid: [] },
        beers: { recommended: ["lager", "ale"], avoid: [] },
        spirits: { recommended: ["whiskey", "gin"], avoid: [] },
        cocktails: { recommended: ["classic", "contemporary"], avoid: [] },
      },
      specialConsiderations: ["Dietary restrictions may apply"],
      seasonalAlignment: "Year-round",
      timestamp: Date.now(),
      menuItemCount: menuItems.length,
      fallback: true,
    };
  }

  fallbackPairings(beverage, menuItems) {
    return menuItems.slice(0, 5).map((item) => ({
      beverage: { name: beverage.name, type: beverage.type },
      dish: { name: item.name, category: item.category },
      confidence: 0.6,
      rationale: "General pairing compatibility based on beverage category",
      servingSuggestions: ["Serve at recommended temperature"],
      alternatives: [],
      timestamp: Date.now(),
      fallback: true,
    }));
  }

  fallbackExplanation(beverage, dish) {
    return {
      beverage: { name: beverage.name, type: beverage.type },
      dish: { name: dish.name, category: dish.category },
      flavorHarmony: "Complementary pairing approach",
      textureInteraction: "Balanced texture interaction",
      pairingStrategy: "complement",
      culturalRationale: "Traditional pairing principles",
      servingRecommendations: ["Serve at appropriate temperature"],
      alternatives: ["Consider similar style beverages"],
      confidence: 0.6,
      confidenceReasoning: "Basic compatibility assessment",
      timestamp: Date.now(),
      fallback: true,
    };
  }

  fallbackCocktailRecipes(spirits) {
    return spirits.slice(0, 3).map((spirit, index) => ({
      name: `${spirit.name} Classic #${index + 1}`,
      description: `Classic cocktail featuring ${spirit.name}`,
      ingredients: [
        `2 oz ${spirit.name}`,
        "0.5 oz simple syrup",
        "0.75 oz fresh lemon juice",
        "Ice",
      ],
      instructions: [
        "Add all ingredients to shaker with ice",
        "Shake vigorously for 15 seconds",
        "Double strain into chilled glass",
      ],
      glassware: "Coupe glass",
      garnish: "Lemon twist",
      difficulty: "Easy",
      prepTime: "3 minutes",
      spiritsUsed: [spirit.name],
      foodPairings: ["Light appetizers", "Seafood"],
      seasonalFit: "Year-round",
      profitMargin: "Good",
      technique: "Shake",
      timestamp: Date.now(),
      fallback: true,
    }));
  }

  fallbackContextualPairings(context, inventory) {
    return inventory.slice(0, 8).map((item) => ({
      beverage: { name: item.name, type: item.type },
      reasoning: `Suitable for ${context.occasion || "general dining"}`,
      contextAlignment: 0.7,
      versatility: 0.7,
      pricePoint: "moderate",
      seasonalFit: context.season || "appropriate",
      occasionSuitability: context.occasion || "suitable",
      groupDiningScore: 0.7,
      alternatives: [],
      timestamp: Date.now(),
      fallback: true,
    }));
  }

  // Cache management methods

  clearCache() {
    this.pairingCache.clear();
    this.menuAnalysisCache.clear();
  }

  getCacheStats() {
    return {
      pairingCacheSize: this.pairingCache.size,
      menuAnalysisCacheSize: this.menuAnalysisCache.size,
      cacheExpiry: this.cacheExpiry,
    };
  }
}

// Export the class for cases where multiple instances are needed
export { IntelligentPairingSystem };

// Create and export singleton instance
export const intelligentPairingSystem = new IntelligentPairingSystem();
export default intelligentPairingSystem;
